
import { useParams, useNavigate } from 'react-router-dom';
import { newsData } from '../../mocks/news';
import Navbar from '../../components/feature/Navbar';

export default function NewsDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const newsItem = newsData.find(item => item.id === parseInt(id || '0'));
  
  if (!newsItem) {
    return (
      <div className="min-h-screen bg-lp-bg flex items-center justify-center">
        <div className="text-center vintage-paper p-8">
          <h2 className="text-2xl font-headings text-sepia-900 mb-4">Artículo no encontrado</h2>
          <button 
            onClick={() => navigate('/')}
            className="vintage-paper-subtle text-sepia-900 hover:bg-sepia-100/70 px-4 py-2 text-sm font-ui transition-all duration-300 rounded-[15%] hover:shadow-md"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  const handleGoBack = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-lp-bg">
      <Navbar />
      
      <div className="pt-20 px-4 pb-8">
        {/* Back Button */}
        <button 
          onClick={handleGoBack}
          className="flex items-center gap-2 text-lp-link hover:text-lp-link-hover font-ui text-sm mb-6 transition-colors duration-200 vintage-paper-subtle px-3 py-1 rounded-[15%] hover:shadow-md"
        >
          <i className="ri-arrow-left-line"></i>
          Volver a noticias
        </button>

        {/* Article Header */}
        <div className="vintage-paper aged-paper p-6 mb-6 shadow-lg">
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="text-xs font-ui text-sepia-600 vintage-paper-subtle px-3 py-1 rounded-[15%]">
              {newsItem.category}
            </span>
            <span className="text-xs text-sepia-500 font-ui vintage-paper-subtle px-2 py-1 rounded-[15%]">
              {newsItem.date}
            </span>
          </div>
          
          <h1 className="font-headings text-2xl text-sepia-900 mb-4 leading-tight">
            {newsItem.title}
          </h1>
          
          <div className="flex justify-between items-center text-sm text-sepia-600 font-ui border-t border-sepia-300/50 pt-4">
            <span>Por: <strong>{newsItem.author}</strong></span>
            <span>Fuente: {newsItem.source}</span>
          </div>
        </div>

        {/* Article Image */}
        <div className="mb-6 overflow-hidden shadow-lg vintage-paper img-vintage-large">
          <img 
            src={`https://readdy.ai/api/search-image?query=vintage%20$%7BnewsItem.category.toLowerCase%28%29%7D%20scene%2C%20classic%20literature%20atmosphere%2C%20antique%20books%20and%20manuscripts%2C%20sepia%20tones%2C%20warm%20lighting%2C%20scholarly%20environment%2C%20old%20library%20setting%2C%20leather-bound%20books%2C%20wooden%20desk%20with%20brass%20details%2C%20nostalgic%20literary%20aesthetic%2C%20aged%20paper%20textures%2C%20classic%20academic%20atmosphere%2C%20warm%20browns%20and%20cream%20colors&width=375&height=200&seq=article_${newsItem.id}&orientation=landscape`}
            alt={newsItem.title}
            className="w-full"
          />
        </div>

        {/* Article Content */}
        <div className="vintage-paper aged-paper p-6 shadow-lg">
          <div className="prose prose-sepia max-w-none">
            <p className="text-lg font-body text-sepia-800 mb-6 leading-relaxed italic border-l-4 border-sepia-400/50 pl-4 vintage-paper-subtle p-3 rounded-r-[10%]">
              {newsItem.excerpt}
            </p>
            
            <div className="space-y-4 text-sepia-700 font-body leading-relaxed">
              {newsItem.category === 'Literatura' && (
                <>
                  <p>Esta obra representa uno de los pilares fundamentales de la literatura universal. A través de sus páginas, el autor nos transporta a un mundo donde la realidad y la ficción se entrelazan de manera magistral, creando una experiencia de lectura que trasciende las barreras del tiempo.</p>
                  
                  <p>La narrativa se desarrolla con una prosa elegante y profunda, característica del estilo único del autor. Cada personaje está meticulosamente construido, reflejando las complejidades de la condición humana y los dilemas morales de su época.</p>
                  
                  <p>El contexto histórico en el que se desarrolla la historia añade capas de significado que enriquecen la interpretación de la obra. Los críticos literarios han destacado la relevancia contemporánea de los temas abordados, desde la alienación individual hasta las estructuras sociales y políticas.</p>
                </>
              )}
              
              {newsItem.category === 'Teatro' && (
                <>
                  <p>Esta nueva interpretación de la obra clásica busca acercar el teatro tradicional a las audiencias contemporáneas, manteniendo la esencia original mientras incorpora elementos visuales y narrativos actuales.</p>
                  
                  <p>La dirección escénica ha logrado crear un equilibrio perfecto entre respeto por el texto original y innovación artística. Los actores entregan interpretaciones que honran la tradición teatral mientras aportan frescura y modernidad al montaje.</p>
                  
                  <p>El diseño de escenografía y vestuario refleja una estética que dialoga entre lo clásico y lo contemporáneo, creando un universo visual único que potencia la experiencia dramática del espectador.</p>
                </>
              )}
              
              {newsItem.category === 'Poesía' && (
                <>
                  <p>La poesía de este autor se caracteriza por su capacidad de capturar las emociones más profundas del ser humano con una sensibilidad extraordinaria. Sus versos trascienden las barreras del tiempo, hablando directamente al corazón del lector contemporáneo.</p>
                  
                  <p>El uso del lenguaje poético revela una maestría técnica que se combina con una autenticidad emocional pocas veces vista en la literatura. Cada poema es un universo completo de sensaciones, imágenes y reflexiones.</p>
                  
                  <p>La influencia de esta obra en generaciones posteriores de poetas es innegable, estableciendo nuevas formas de expresión lírica que continúan inspirando a artistas de todo el mundo.</p>
                </>
              )}
              
              {newsItem.category === 'Ensayo' && (
                <>
                  <p>Este ensayo presenta una reflexión profunda sobre temas fundamentales de la experiencia humana. El autor desarrolla sus ideas con rigor intelectual y una prosa elegante que hace accesibles conceptos complejos.</p>
                  
                  <p>La argumentación se construye de manera sistemática, presentando evidencias y análisis que sustentan las conclusiones del autor. Su enfoque interdisciplinario enriquece la perspectiva y ofrece una visión integral del tema tratado.</p>
                  
                  <p>La relevancia contemporánea de estas reflexiones demuestra la vigencia del pensamiento del autor, cuyas ideas continúan siendo objeto de debate y análisis en círculos académicos y culturales.</p>
                </>
              )}
              
              {(newsItem.category === 'Noticias' || newsItem.category === 'Blog') && (
                <>
                  <p>Este artículo presenta información actualizada y relevante para la comunidad literaria. Los datos presentados han sido verificados y provienen de fuentes confiables dentro del ámbito académico y cultural.</p>
                  
                  <p>La investigación detrás de esta publicación refleja el compromiso con la excelencia académica y la divulgación responsable del conocimiento literario. Los expertos consultados aportan perspectivas valiosas que enriquecen el análisis.</p>
                  
                  <p>Las implicaciones de esta información trascienden el ámbito académico, ofreciendo insights importantes para lectores, escritores and estudiosos de la literatura en general.</p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Source and Contact */}
        <div 
          onClick={() => window.open(`https://${newsItem.source}`, '_blank')}
          className="mt-6 vintage-paper-subtle p-4 cursor-pointer hover:bg-sepia-100/70 transition-all duration-300 hover:shadow-md"
        >
          <h3 className="font-headings text-sm text-sepia-800 mb-2">VER REFERENCIA ORIGINAL</h3>
          <div className="text-xs text-sepia-600 font-ui space-y-1">
            <p><strong>Fuente:</strong> {newsItem.source}</p>
            <p><strong>Contacto:</strong> {newsItem.email}</p>
            <p><strong>Fecha de publicación:</strong> {newsItem.date}</p>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-8">
          <h3 className="font-headings text-lg text-sepia-900 border-b border-sepia-400/50 pb-2 mb-4">
            ARTÍCULOS RELACIONADOS
          </h3>
          <div className="space-y-3">
            {newsData
              .filter(item => item.category === newsItem.category && item.id !== newsItem.id)
              .slice(0, 3)
              .map(relatedItem => (
                <div 
                  key={relatedItem.id}
                  onClick={() => navigate(`/news/${relatedItem.id}`)}
                  className="vintage-paper-subtle p-3 cursor-pointer hover:bg-sepia-100/70 transition-all duration-300 hover:shadow-md"
                >
                  <h4 className="font-headings text-sm text-sepia-800 mb-1">
                    {relatedItem.title}
                  </h4>
                  <p className="text-xs text-sepia-600 font-ui">
                    Por {relatedItem.author} • {relatedItem.date}
                  </p>
                </div>
              ))
            }
          </div>
        </div>

        {/* Vintage Paper Texture Effect */}
        <div 
          className="fixed inset-0 pointer-events-none opacity-5 z-0 img-vintage-overlay"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=vintage%20paper%20texture%2C%20old%20book%20pages%2C%20antique%20parchment%20background%2C%20sepia%20tones%2C%20weathered%20paper%20with%20subtle%20stains%20and%20aging%20marks%2C%20literary%20atmosphere%2C%20warm%20beige%20and%20cream%20colors%2C%20high%20resolution%20texture%2C%20nostalgic%20feel%2C%20classic%20book%20aesthetic&width=375&height=812&seq=vintage_bg_detail&orientation=portrait')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'multiply'
          }}
        />
      </div>
    </div>
  );
}
